import { ClockCircleOutlined } from '@ant-design/icons';
import { Tooltip, Typography } from 'antd';
import classNames from 'classnames';
import { type KeyboardEvent, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Routes } from 'router/routes';

import { getMethodTagClassName, renderMethodIcon } from '../utils/method-tag';
import styles from './styles.module.scss';

import type { TAppointmentRowProps } from './types';

const { Text } = Typography;

const AppointmentRow = ({
  appointment,
  actionButtons,
}: TAppointmentRowProps) => {
  const navigate = useNavigate();
  const {
    patientName,
    birthDateLabel,
    languageLabel,
    methodTag,
    timeLabel,
    subtimeLabel,
  } = appointment;
  const canNavigate = Boolean(appointment.patientGuid);
  const rowAriaLabel = `View appointment for ${patientName?.replace(
    /[<>"'&]/g,
    '',
  )}`;

  const handleRowNavigation = useCallback(() => {
    if (!appointment.patientGuid) return;
    navigate(Routes.patientGuid(appointment.patientGuid));
  }, [appointment.patientGuid, navigate]);

  const handleRowKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!canNavigate) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleRowNavigation();
      }
    },
    [canNavigate, handleRowNavigation],
  );

  return (
    <div
      className={classNames(styles.row, {
        [styles['row-clickable']]: canNavigate,
      })}
      {...(canNavigate && {
        role: 'button',
        tabIndex: 0,
        'aria-label': rowAriaLabel,
        onClick: handleRowNavigation,
        onKeyDown: handleRowKeyDown,
      })}
    >
      <div className={styles['row-main']}>
        <div className={styles['patient-line']}>
          <Text className={styles['patient-name']}>{patientName}</Text>
          {birthDateLabel || languageLabel ? (
            <div className={styles['patient-info']}>
              {birthDateLabel ? (
                <Text className={styles['patient-info-text']}>
                  {birthDateLabel}
                </Text>
              ) : null}
              {birthDateLabel && languageLabel ? (
                <span className={styles['meta-divider']} aria-hidden>
                  |
                </span>
              ) : null}
              {languageLabel ? (
                <Text className={styles['patient-info-text']}>
                  {languageLabel}
                </Text>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className={styles.time}>
          <ClockCircleOutlined className={styles['time-icon']} aria-hidden />
          <Text className={styles['time-label']}>{timeLabel}</Text>
          <Text className={styles['subtime-label']}>(CST: {subtimeLabel})</Text>
        </div>

        <div className={styles['row-meta']}>
          {methodTag ? (
            <span
              className={classNames(
                styles['method-tag'],
                getMethodTagClassName(styles, methodTag.variant),
              )}
            >
              {renderMethodIcon(methodTag.variant)}
              {methodTag.label}
            </span>
          ) : null}
        </div>
      </div>

      {actionButtons?.length ? (
        <div className={styles.actions}>
          {actionButtons
            .filter((action) => {
              if (action.id === 'calendar') {
                return appointment?.isCcmEnrolled === true;
              }
              return true;
            })
            .map((action) => (
              <Tooltip
                key={action.id}
                title={action.tooltip}
                placement="bottom"
              >
                <button
                  key={action.id}
                  type="button"
                  className={styles['action-button']}
                  aria-label={action.ariaLabel}
                  onClick={(event) => {
                    event.stopPropagation();
                    action.onClick?.(appointment);
                  }}
                >
                  <span className={styles['action-icon']} aria-hidden>
                    {action.icon}
                  </span>
                </button>
              </Tooltip>
            ))}
        </div>
      ) : null}
    </div>
  );
};

export default AppointmentRow;
